// Column Mappings dialog.
//
// Per-merchant editor for deterministic column → canonical-field mappings.
// Opened from the table-tools button on a merchant row.
//
// Flow:
//   - Left column: list of saved mappings (one per (format, name)).
//   - Right column: edit form for the selected one.
//   - "Add new" → empty draft scoped to a chosen format.
//   - "Test" against an existing artifact id → preview the canonical fields
//     the mapping would produce (uses POST /test).
//
// Why this matters: when a merchant exports clean XLSX/CSV with predictable
// column names, the document pipeline uses this mapping FIRST — no Gemini
// call, no hallucinations, and per-document cost drops to zero.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Stack, Paper, Typography, IconButton, Button, Chip, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Select, FormControl, InputLabel, ListSubheader,
    Alert, Divider, Tabs, Tab, alpha, Switch, FormControlLabel,
    LinearProgress, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
    rpaCanonicalFields, rpaMappingList, rpaMappingCreate,
    rpaMappingUpdate, rpaMappingDelete, rpaMappingTest,
} from './api';
import {
    SectionPaper, EmptyState, RPA_COLORS, formatDateLong,
} from './rpaTheme';


const FORMAT_OPTIONS = [
    { value: 'XLSX', label: 'XLSX (Excel)' },
    { value: 'CSV',  label: 'CSV' },
    { value: 'PDF_TEXT', label: 'PDF (text-based) — preview only' },
    { value: 'HTML', label: 'HTML' },
];

const EMPTY_DRAFT = (mapping_id = null) => ({
    id: mapping_id,
    format: 'XLSX',
    name: 'Default',
    header_row: 1,
    sheet_name: '',
    is_active: true,
    mappings: [
        { source: '', target: 'amount', transform: 'currency', default: '' },
    ],
    vendor_constants_text: '',
});


export default function ColumnMappingsDialog({ open, merchant, onClose }) {
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [canonicalFields, setCanonicalFields] = useState([]);
    // Rich metadata: [{name, label, group, help}, ...]. Falls back to a
    // synthetic version derived from `canonicalFields` if the server
    // hasn't been redeployed yet.
    const [canonicalFieldMeta, setCanonicalFieldMeta] = useState([]);
    const [transformOpts, setTransformOpts] = useState([]);
    const [draft, setDraft] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [testArtifactId, setTestArtifactId] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [testRunning, setTestRunning] = useState(false);

    const merchantId = merchant?.id;

    const load = useCallback(async () => {
        if (!merchantId) return;
        setLoading(true);
        setError(null);
        try {
            const [list, canon] = await Promise.all([
                rpaMappingList(merchantId),
                rpaCanonicalFields(),
            ]);
            if (list?.data?.success) setMappings(list.data.data || []);
            if (canon?.data?.success) {
                const fields = canon.data.data.fields || [];
                setCanonicalFields(fields);
                setTransformOpts(canon.data.data.transforms || []);
                // Use server-provided metadata when available; otherwise
                // synthesize a minimal version so old API responses still
                // render a sensible dropdown.
                const meta = canon.data.data.fields_metadata;
                setCanonicalFieldMeta(
                    Array.isArray(meta) && meta.length > 0
                        ? meta
                        : fields.map((f) => ({
                            name: f, label: f,
                            group: f.startsWith('line_item.') ? 'Line items' : 'Document',
                            help: '',
                        }))
                );
            }
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [merchantId]);

    useEffect(() => { if (open) load(); }, [open, load]);

    useEffect(() => {
        if (!open) {
            setDraft(null);
            setTestResult(null);
            setTestArtifactId('');
        }
    }, [open]);

    const startNew = () => {
        setDraft(EMPTY_DRAFT());
        setTestResult(null);
    };

    const startEdit = (m) => {
        setDraft({
            id: m.id,
            format: m.format,
            name: m.name,
            header_row: m.header_row,
            sheet_name: m.sheet_name || '',
            is_active: !!m.is_active,
            mappings: (m.mappings && m.mappings.length > 0)
                ? m.mappings.map((r) => ({
                    source: r.source || '',
                    target: r.target || 'amount',
                    transform: r.transform || '',
                    default: r.default ?? '',
                }))
                : EMPTY_DRAFT().mappings,
            vendor_constants_text: m.vendor_constants
                ? JSON.stringify(m.vendor_constants, null, 2) : '',
        });
        setTestResult(null);
    };

    const cancelDraft = () => { setDraft(null); setTestResult(null); };

    const remove = async (m) => {
        if (!window.confirm(`Delete mapping "${m.name}" (${m.format})?`)) return;
        try {
            await rpaMappingDelete(merchantId, m.id);
            load();
            if (draft?.id === m.id) setDraft(null);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const save = async () => {
        if (!draft) return;
        setError(null);

        let constants = null;
        if (draft.vendor_constants_text.trim()) {
            try { constants = JSON.parse(draft.vendor_constants_text); }
            catch { setError('Vendor constants must be valid JSON or empty.'); return; }
        }

        const cleanMappings = draft.mappings
            .filter((r) => r.source.trim() && r.target)
            .map((r) => {
                const out = { source: r.source.trim(), target: r.target };
                if (r.transform) out.transform = r.transform;
                if (r.default !== '' && r.default !== null && r.default !== undefined) {
                    out.default = r.default;
                }
                return out;
            });

        if (cleanMappings.length === 0) {
            setError('Add at least one mapping rule with a source column.');
            return;
        }

        const payload = {
            format: draft.format,
            name: draft.name.trim() || 'Default',
            header_row: parseInt(draft.header_row, 10) || 1,
            sheet_name: draft.sheet_name.trim() || null,
            is_active: !!draft.is_active,
            mappings: cleanMappings,
            vendor_constants: constants,
        };

        setSubmitting(true);
        try {
            if (draft.id) {
                const r = await rpaMappingUpdate(merchantId, draft.id, payload);
                if (!r?.data?.success) throw new Error(r?.data?.message || 'Save failed');
            } else {
                const r = await rpaMappingCreate(merchantId, payload);
                if (!r?.data?.success) throw new Error(r?.data?.message || 'Save failed');
            }
            await load();
            setDraft(null);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const runTest = async () => {
        if (!draft?.id || !testArtifactId) return;
        setTestRunning(true);
        setTestResult(null);
        setError(null);
        try {
            const r = await rpaMappingTest(merchantId, draft.id, {
                artifact_id: parseInt(testArtifactId, 10),
            });
            if (!r?.data?.success) throw new Error(r?.data?.message || 'Test failed');
            setTestResult(r.data.data);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setTestRunning(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <TableChartIcon sx={{ color: RPA_COLORS.robot }} />
                    <Typography variant="h6">
                        Column Mappings — {merchant?.name}
                    </Typography>
                </Stack>
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                {error && (
                    <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {loading && <LinearProgress />}

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
                    minHeight: 460,
                }}>
                    {/* LEFT — list of mappings */}
                    <Box sx={{
                        borderRight: { md: `1px solid ${RPA_COLORS.border}` },
                        background: RPA_COLORS.surfaceMuted,
                    }}>
                        <Stack direction="row" alignItems="center"
                               sx={{ p: 1.5, borderBottom: `1px solid ${RPA_COLORS.border}` }}>
                            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
                                Saved mappings ({mappings.length})
                            </Typography>
                            <Button size="small" startIcon={<AddIcon />}
                                    variant="contained" onClick={startNew}>
                                New
                            </Button>
                        </Stack>

                        {mappings.length === 0 && !loading && (
                            <Box sx={{ p: 3, textAlign: 'center',
                                       color: RPA_COLORS.textMuted }}>
                                <Typography variant="body2">
                                    No mappings yet. Click <strong>New</strong> to define
                                    one — useful when the merchant exports XLSX or CSV with
                                    predictable column headers.
                                </Typography>
                            </Box>
                        )}

                        {mappings.map((m) => {
                            const active = draft?.id === m.id;
                            return (
                                <Box
                                    key={m.id}
                                    onClick={() => startEdit(m)}
                                    sx={{
                                        px: 2, py: 1.25,
                                        cursor: 'pointer',
                                        borderLeft: active
                                            ? `3px solid ${RPA_COLORS.accent}`
                                            : '3px solid transparent',
                                        background: active
                                            ? alpha(RPA_COLORS.accent, 0.10)
                                            : 'transparent',
                                        borderBottom: `1px solid ${alpha(RPA_COLORS.border, 0.5)}`,
                                        '&:hover': {
                                            background: alpha(RPA_COLORS.accent, 0.06),
                                        },
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {m.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                                {m.format} · {m.mappings.length} rule{m.mappings.length === 1 ? '' : 's'}
                                            </Typography>
                                        </Box>
                                        <Chip size="small"
                                              label={m.is_active ? 'Active' : 'Off'}
                                              color={m.is_active ? 'primary' : 'default'}
                                              variant={m.is_active ? 'filled' : 'outlined'} />
                                        <IconButton size="small" color="error"
                                                    onClick={(e) => { e.stopPropagation(); remove(m); }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* RIGHT — edit form */}
                    <Box sx={{ p: 2.5 }}>
                        {!draft ? (
                            <EmptyState
                                icon={<TableChartIcon />}
                                title="Pick a mapping to edit, or create a new one"
                                message="Mappings let the agent extract canonical fields directly from XLSX or CSV columns — no Gemini call, no hallucinations, instant."
                            />
                        ) : (
                            <DraftEditor
                                draft={draft}
                                setDraft={setDraft}
                                canonicalFields={canonicalFields}
                                canonicalFieldMeta={canonicalFieldMeta}
                                transformOpts={transformOpts}
                                onCancel={cancelDraft}
                                onSave={save}
                                submitting={submitting}
                                testArtifactId={testArtifactId}
                                setTestArtifactId={setTestArtifactId}
                                onRunTest={runTest}
                                testRunning={testRunning}
                                testResult={testResult}
                            />
                        )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}


function DraftEditor({
    draft, setDraft, canonicalFields, canonicalFieldMeta, transformOpts,
    onCancel, onSave, submitting,
    testArtifactId, setTestArtifactId, onRunTest, testRunning, testResult,
}) {
    // Group canonical fields by `group` so the Select renders sub-headers
    // (Document vs Line items). This is what makes `description` (document)
    // visually distinct from `line_item.description` (per-row).
    const fieldGroups = React.useMemo(() => {
        const map = new Map();
        for (const f of canonicalFieldMeta || []) {
            const g = f.group || 'Other';
            if (!map.has(g)) map.set(g, []);
            map.get(g).push(f);
        }
        return Array.from(map.entries());  // [['Document', [...]], ['Line items', [...]]]
    }, [canonicalFieldMeta]);
    const fieldByName = React.useMemo(() => {
        const m = new Map();
        for (const f of canonicalFieldMeta || []) m.set(f.name, f);
        return m;
    }, [canonicalFieldMeta]);
    const set = (patch) => setDraft({ ...draft, ...patch });

    const setRule = (idx, patch) => {
        const next = [...draft.mappings];
        next[idx] = { ...next[idx], ...patch };
        setDraft({ ...draft, mappings: next });
    };

    const addRule = () => setDraft({
        ...draft,
        mappings: [...draft.mappings,
                   { source: '', target: pickNextTarget(draft.mappings, canonicalFields),
                     transform: '', default: '' }],
    });

    const removeRule = (idx) =>
        setDraft({ ...draft, mappings: draft.mappings.filter((_, i) => i !== idx) });

    const moveRule = (idx, delta) => {
        const newIdx = idx + delta;
        if (newIdx < 0 || newIdx >= draft.mappings.length) return;
        const next = [...draft.mappings];
        [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
        setDraft({ ...draft, mappings: next });
    };

    const isXlsxOrCsv = draft.format === 'XLSX' || draft.format === 'CSV';

    return (
        <Stack spacing={2.5}>
            {/* Header form */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Format</InputLabel>
                    <Select label="Format" value={draft.format}
                            onChange={(e) => set({ format: e.target.value })}>
                        {FORMAT_OPTIONS.map((f) => (
                            <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField size="small" label="Name" sx={{ flex: 1 }}
                           value={draft.name}
                           onChange={(e) => set({ name: e.target.value })}
                           placeholder="Default" />
                <FormControlLabel
                    control={<Switch checked={draft.is_active}
                                     onChange={(e) => set({ is_active: e.target.checked })} />}
                    label="Active" />
            </Stack>

            {isXlsxOrCsv && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        size="small" type="number" label="Header row (1-indexed)"
                        value={draft.header_row}
                        onChange={(e) => set({ header_row: e.target.value })}
                        sx={{ width: 180 }}
                    />
                    {draft.format === 'XLSX' && (
                        <TextField
                            size="small" label="Sheet name (optional)"
                            value={draft.sheet_name}
                            onChange={(e) => set({ sheet_name: e.target.value })}
                            placeholder="Leave blank for first sheet"
                            sx={{ flex: 1 }}
                        />
                    )}
                </Stack>
            )}

            {!isXlsxOrCsv && (
                <Alert severity="info">
                    Column mapping for <strong>{draft.format}</strong> is currently
                    a preview-only placeholder. The pipeline will fall through to
                    Gemini for these formats. XLSX and CSV mappings run
                    deterministically end-to-end.
                </Alert>
            )}

            {/* Mapping rules */}
            <Box>
                <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
                        Column → field mapping ({draft.mappings.length})
                    </Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={addRule}>
                        Add rule
                    </Button>
                </Stack>

                <Stack spacing={1}>
                    {draft.mappings.map((rule, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 1.25 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}
                                   alignItems={{ md: 'center' }}>
                                <TextField
                                    size="small" label="Source column" sx={{ flex: 1.2 }}
                                    value={rule.source}
                                    onChange={(e) => setRule(idx, { source: e.target.value })}
                                    placeholder='e.g. "Doc #"'
                                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                                />
                                <Box sx={{ color: RPA_COLORS.textFaint, fontSize: 18, px: 0.5 }}>→</Box>
                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel>Canonical field</InputLabel>
                                    <Select
                                        label="Canonical field"
                                        value={rule.target}
                                        onChange={(e) => setRule(idx, { target: e.target.value })}
                                        renderValue={(value) => {
                                            const meta = fieldByName.get(value);
                                            return meta ? meta.label : value;
                                        }}
                                    >
                                        {fieldGroups.length === 0 && canonicalFields.map((f) => (
                                            <MenuItem key={f} value={f}>{f}</MenuItem>
                                        ))}
                                        {fieldGroups.flatMap(([group, fields], gi) => [
                                            <ListSubheader
                                                key={`hdr-${group}`}
                                                sx={{
                                                    lineHeight: '24px',
                                                    fontWeight: 700,
                                                    fontSize: 11,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.6,
                                                    color: RPA_COLORS.textMuted,
                                                    background: gi === 0 ? 'transparent' : '#FAFAFC',
                                                }}
                                            >
                                                {group}
                                            </ListSubheader>,
                                            ...fields.map((f) => (
                                                <MenuItem
                                                    key={f.name}
                                                    value={f.name}
                                                    sx={{ alignItems: 'flex-start', py: 0.75 }}
                                                >
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {f.label}
                                                        </Typography>
                                                        {f.help && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display: 'block',
                                                                    color: RPA_COLORS.textMuted,
                                                                    whiteSpace: 'normal',
                                                                    lineHeight: 1.3,
                                                                }}
                                                            >
                                                                {f.help}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            )),
                                        ])}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ width: 130 }}>
                                    <InputLabel>Transform</InputLabel>
                                    <Select label="Transform" value={rule.transform}
                                            onChange={(e) => setRule(idx, { transform: e.target.value })}>
                                        <MenuItem value=""><em>Auto</em></MenuItem>
                                        {transformOpts.map((t) => (
                                            <MenuItem key={t} value={t}>{t}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small" label="Default" sx={{ width: 130 }}
                                    value={rule.default ?? ''}
                                    onChange={(e) => setRule(idx, { default: e.target.value })}
                                    placeholder="—"
                                />
                                <Stack direction="row">
                                    <Tooltip title="Move up">
                                        <span>
                                            <IconButton size="small"
                                                        onClick={() => moveRule(idx, -1)}
                                                        disabled={idx === 0}>
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Move down">
                                        <span>
                                            <IconButton size="small"
                                                        onClick={() => moveRule(idx, 1)}
                                                        disabled={idx === draft.mappings.length - 1}>
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Remove rule">
                                        <span>
                                            <IconButton size="small" color="error"
                                                        onClick={() => removeRule(idx)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            </Box>

            {/* Vendor constants */}
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Vendor constants (optional)
                </Typography>
                <TextField
                    fullWidth multiline minRows={2} size="small"
                    placeholder='{ "vendor_name": "Acme Utility Co", "currency": "USD" }'
                    value={draft.vendor_constants_text}
                    onChange={(e) => set({ vendor_constants_text: e.target.value })}
                    sx={{ fontFamily: 'monospace' }}
                />
                <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                    Pinned values merged onto the extracted document. Constants do
                    NOT override fields the mapping populated from the file.
                </Typography>
            </Box>

            {/* Test against an artifact */}
            {draft.id && (
                <Paper variant="outlined" sx={{ p: 2, background: RPA_COLORS.surfaceMuted }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Test against an existing artifact
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small" label="Artifact ID" sx={{ width: 180 }}
                            value={testArtifactId}
                            onChange={(e) => setTestArtifactId(e.target.value)}
                            placeholder="e.g. 4123"
                            type="number"
                        />
                        <Button variant="outlined" startIcon={<PlayArrowIcon />}
                                onClick={onRunTest}
                                disabled={testRunning || !testArtifactId}>
                            {testRunning ? 'Running…' : 'Run test'}
                        </Button>
                    </Stack>
                    {testResult && (
                        <Box sx={{ mt: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Chip
                                    size="small"
                                    label={testResult.matched ? 'Matched' : 'Did not match'}
                                    color={testResult.matched ? 'success' : 'warning'} />
                                <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                    Tested at {formatDateLong(testResult.tested_at)}
                                </Typography>
                            </Stack>
                            {testResult.matched
                                ? renderCanonicalTable(testResult.canonical || {})
                                : null}
                            {(testResult.issues || []).length > 0 && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    {testResult.issues.map((iss, i) =>
                                        <div key={i}>{iss}</div>
                                    )}
                                </Alert>
                            )}
                        </Box>
                    )}
                </Paper>
            )}

            {/* Save / cancel */}
            <Divider />
            <Stack direction="row" spacing={1}>
                <Button onClick={onCancel} disabled={submitting}>Cancel</Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" startIcon={<SaveIcon />}
                        onClick={onSave} disabled={submitting}
                        sx={{ textTransform: 'none', fontWeight: 600,
                              background: `linear-gradient(135deg, ${RPA_COLORS.robot}, ${RPA_COLORS.accent})` }}>
                    {submitting ? 'Saving…' : draft.id ? 'Save changes' : 'Create mapping'}
                </Button>
            </Stack>
        </Stack>
    );
}


function renderCanonicalTable(canonical) {
    const entries = Object.entries(canonical);
    if (entries.length === 0) {
        return (
            <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                No fields populated.
            </Typography>
        );
    }
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Value</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {entries.map(([k, v]) => (
                    <TableRow key={k}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{k}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{String(v)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}


function pickNextTarget(existing, canonicalFields) {
    const used = new Set(existing.map((r) => r.target));
    return canonicalFields.find((f) => !used.has(f)) || canonicalFields[0] || 'amount';
}
